import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { alpha, styled } from '@mui/material/styles';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import FuseNavBadge from '../../FuseNavBadge';
import FuseSvgIcon from '../../../FuseSvgIcon';
import { accessPriority } from 'src/app/constants/accessPermission';

const Root = styled(ListItem)(({ theme, isDarkMode, itempadding, ...props }) => ({
  minHeight: 44,
  width: '100%',
  borderRadius: '6px',
  margin: '0 0 4px 0',
  paddingRight: 16,
  paddingLeft: itempadding > 80 ? 80 : itempadding,
  paddingTop: 10,
  paddingBottom: 10,
  color: alpha(theme.palette.text.primary, 0.7),
  cursor: 'pointer',
  textDecoration: 'none!important',
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '&.active': {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, .05)!important'
        : 'rgba(255, 255, 255, .1)!important',
    pointerEvents: 'none',
    transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
    '& > .fuse-list-item-text-primary': {
      color: 'inherit',
    },
    '& > .fuse-list-item-icon': {
      color: isDarkMode ? 'lightgrey' : 'dark',
    },
  },
  '& >.fuse-list-item-icon': {
    marginRight: 16,
    color: isDarkMode ? 'lightgrey' : 'dark',
  },
}));
function FuseNavVerticalItem(props) {
  const permissions = useSelector((state) => state.permissionSlice.permissions);
  const { item, nestedLevel, onItemClick } = props;
  const isDarkMode = useSelector((state) => state.themeSlice.isDarkMode);

  const maxAccess = item?.permissionKey
    ? permissions?.reduce((max, role) => {
        const perm = role.permissions?.find((p) => p.key === item.permissionKey);
        if (perm) {
          return Math.max(max, accessPriority[perm.access] ?? 0);
        }
        return max;
      }, 0)
    : 2;

  if (maxAccess === accessPriority.Disable) {
    return null;
  }

  const isDisabled = maxAccess === accessPriority.Read;
  const itempadding = nestedLevel > 0 ? 38 + nestedLevel * 16 : 16;

  return (
    <Root
      button
      component={NavLinkAdapter}
      to={isDisabled ? '' : item.url || ''}
      // to={item.url}
      activeClassName={item.url ? 'active' : ''}
      className={clsx('fuse-list-item', item.active && 'active')}
      onClick={() => {
        if (!isDisabled && onItemClick) {
          onItemClick(item);
        }
      }}
      end={item.end}
      itempadding={itempadding}
      isDarkMode={isDarkMode}
      role="button"
      sx={item.sx}
      disabled={isDisabled}
    >
      {item.icon && (
        <FuseSvgIcon
          className={clsx('fuse-list-item-icon shrink-0', item.iconClass)}
          color="action"
        >
          {item.icon}
        </FuseSvgIcon>
      )}
      {item.title.length > 25 ? (
        <Tooltip title={item.title}>
          <ListItemText
            className="fuse-list-item-text"
            primary={item.title}
            secondary={item.subtitle}
            classes={{
              primary: 'text-13 font-medium fuse-list-item-text-primary truncate',
              secondary:
                'text-11 font-medium fuse-list-item-text-secondary leading-normal truncate',
            }}
          />
        </Tooltip>
      ) : (
        <ListItemText
          className="fuse-list-item-text"
          primary={item.title}
          secondary={item.subtitle}
          classes={{
            primary: 'text-13 font-medium fuse-list-item-text-primary truncate',
            secondary: 'text-11 font-medium fuse-list-item-text-secondary leading-normal truncate',
          }}
        />
      )}

      {item.badge && <FuseNavBadge badge={item.badge} />}
    </Root>
  );
}

FuseNavVerticalItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string,
    url: PropTypes.string,
  }),
};

FuseNavVerticalItem.defaultProps = {};

const NavVerticalItem = FuseNavVerticalItem;

export default NavVerticalItem;
